/**
 * useInterviews.ts - 조직원면담 CRUD 및 서명 업로드 Hook
 *
 * 면담 기록 생성/조회/수정 및 서명 이미지 Storage 업로드 기능 제공
 *
 * 컬렉션: interviews
 * 스토리지 경로: interviewSignatures/{companyCode}/{interviewerId}/{interviewId}/{timestamp}.png
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { getSignatureUrl } from '../utils/storage';
export { getSignatureUrl };

// ============================================
// 타입 정의
// ============================================

/** 면담 상태 */
export type InterviewStatus = 'saved';

/** 면담 타입 (OrgInterviewsPage와 동일) */
export type InterviewType =
  | 'regular'
  | 'probation'
  | 'performance'
  | 'resignation'
  | 'return'
  | 'feedback'
  | 'grievance'
  | 'etc';

/** 면담 타입 라벨 매핑 */
export const INTERVIEW_TYPE_LABELS: Record<InterviewType, string> = {
  regular: '정기 면담',
  probation: '수습사원 면담',
  performance: '성과/평가 면담',
  resignation: '퇴직 면담',
  return: '복귀 면담',
  feedback: '피드백 면담',
  grievance: '직원고충 면담',
  etc: '기타',
};

/** 전송/합의 상태 */
export type DeliveryStatus = 'notSent' | 'sent' | 'agreed' | 'disagreed';

/** 전송 상태 라벨 매핑 */
export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  notSent: '미전송',
  sent: '미확인',
  agreed: '상호합의',
  disagreed: '미동의',
};

/** 면담 문서 타입 */
export interface Interview {
  id: string;
  companyCode: string;
  orgUnitId?: string;

  // 대상자 정보
  targetUserId: string;
  targetUserName: string;
  targetUserOrgUnitName?: string;
  targetUserPositionTitle?: string;

  // 면담자(작성자) 정보
  interviewerId: string;
  interviewerName: string;
  interviewerOrgUnitName?: string;

  // 면담 일시/장소
  interviewDate: string;    // YYYY-MM-DD
  interviewTime: string;    // HH:mm
  interviewDateTime?: string; // ISO string (서버 계산)
  location: string;

  // 면담 타입
  type: InterviewType;
  typeLabel: string;

  // 면담 내용
  contentHtml: string;

  // 서명
  interviewerSignaturePath?: string;  // Storage 경로
  interviewerSignatureUrl?: string;   // Download URL (조회 시)

  // 상태
  status: InterviewStatus;

  // 전송/합의 상태
  deliveryStatus?: DeliveryStatus;
  deliveryStatusLabel?: string;
  sentAt?: string;
  sentBy?: string;

  // 대상자 서명 및 동의/미동의
  targetSignaturePath?: string;
  agreedAt?: string;
  disagreedAt?: string;
  disagreementReason?: string;

  // 타임스탬프
  createdAt: string;
  updatedAt: string;
}

/** 면담 생성 요청 - status는 항상 'saved'로 고정 */
export interface CreateInterviewRequest {
  targetUserId: string;
  targetUserName: string;
  targetUserOrgUnitName?: string;
  targetUserPositionTitle?: string;
  interviewDate: string;
  interviewTime: string;
  location: string;
  type: InterviewType;
  contentHtml: string;
  status: 'saved';  // 항상 'saved' (draft 제거)
  signatureBase64?: string;  // 서명 이미지 (base64)
}

/** 면담 수정 요청 - 추가 필드 지원, draft 제거 */
export interface UpdateInterviewRequest {
  id: string;
  contentHtml?: string;
  status?: 'saved';  // 항상 'saved' (draft 제거)
  signatureBase64?: string;
  // 수정 모드 지원
  interviewDate?: string;
  interviewTime?: string;
  location?: string;
  type?: InterviewType;
}

// ============================================
// 응답 타입
// ============================================

interface CreateInterviewResponse {
  success: boolean;
  id: string;
  message?: string;
}

interface UpdateInterviewResponse {
  success: boolean;
  message?: string;
}

interface GetInterviewsResponse {
  success: boolean;
  interviews: Interview[];
}

interface GetInterviewDetailResponse {
  success: boolean;
  interview: Interview;
}

// ============================================
// 면담 목록 조회 Hook
// ============================================

export interface UseInterviewsResult {
  interviews: Interview[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * 특정 직원의 면담 목록 조회
 *
 * @param targetUserId 대상 직원 ID
 */
export function useInterviews(targetUserId: string | null): UseInterviewsResult {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchInterviews = useCallback(async () => {
    if (!targetUserId || !functions) {
      setInterviews([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getInterviews = httpsCallable<
        { targetUserId: string },
        GetInterviewsResponse
      >(functions, 'getInterviews');

      const result = await getInterviews({ targetUserId });

      if (result.data.success) {
        setInterviews(result.data.interviews);
      } else {
        throw new Error('면담 목록 조회 실패');
      }
    } catch (err: any) {
      console.error('[useInterviews] Error:', err);
      setError(err);
      setInterviews([]);
    } finally {
      setIsLoading(false);
    }
  }, [targetUserId]);

  useEffect(() => {
    fetchInterviews();
  }, [fetchInterviews]);

  return {
    interviews,
    isLoading,
    error,
    refetch: fetchInterviews,
  };
}

// ============================================
// 면담 동의 함수
// ============================================

interface AgreeInterviewResponse {
  success: boolean;
  message?: string;
}

/**
 * 면담 동의 (상호합의)
 * @param id 면담 문서 ID
 * @param signatureBase64 대상자 서명 (base64, 선택)
 */
export async function agreeInterview(id: string, signatureBase64?: string): Promise<void> {
  if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

  const agreeFn = httpsCallable<{ id: string; signatureBase64?: string }, AgreeInterviewResponse>(
    functions,
    'agreeInterview'
  );

  try {
    const result = await agreeFn({ id, signatureBase64 });

    if (!result.data.success) {
      throw new Error(result.data.message || '면담 동의 실패');
    }
  } catch (err: any) {
    console.error('[agreeInterview] Error:', {
      code: err?.code,
      message: err?.message,
      details: err?.details,
    });
    throw err;
  }
}

// ============================================
// 면담 미동의 함수
// ============================================

interface DisagreeInterviewResponse {
  success: boolean;
  message?: string;
}

/**
 * 면담 미동의 (반송)
 * @param id 면담 문서 ID
 * @param reason 미동의 사유
 */
export async function disagreeInterview(id: string, reason: string): Promise<void> {
  if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

  const disagreeFn = httpsCallable<{ id: string; reason: string }, DisagreeInterviewResponse>(
    functions,
    'disagreeInterview'
  );

  try {
    const result = await disagreeFn({ id, reason });

    if (!result.data.success) {
      throw new Error(result.data.message || '면담 미동의 실패');
    }
  } catch (err: any) {
    console.error('[disagreeInterview] Error:', {
      code: err?.code,
      message: err?.message,
      details: err?.details,
    });
    throw err;
  }
}

// ============================================
// 면담 상세 조회 Hook
// ============================================

export interface UseInterviewDetailResult {
  interview: Interview | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useInterviewDetail(interviewId: string | null): UseInterviewDetailResult {
  const [interview, setInterview] = useState<Interview | null>(null);
  // interviewId가 있으면 초기 로딩 상태를 true로 설정
  // 이렇게 해야 첫 렌더링에서 RichTextEditor가 기본값으로 초기화되지 않음
  const [isLoading, setIsLoading] = useState(!!interviewId);
  const [error, setError] = useState<Error | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!interviewId || !functions) {
      setInterview(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getDetail = httpsCallable<
        { id: string },
        GetInterviewDetailResponse
      >(functions, 'getInterviewDetail');

      const result = await getDetail({ id: interviewId });

      if (result.data.success) {
        setInterview(result.data.interview);
      } else {
        throw new Error('면담 상세 조회 실패');
      }
    } catch (err: any) {
      console.error('[useInterviewDetail] Error:', err);
      setError(err);
      setInterview(null);
    } finally {
      setIsLoading(false);
    }
  }, [interviewId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    interview,
    isLoading,
    error,
    refetch: fetchDetail,
  };
}

// ============================================
// 면담 Mutations Hook
// ============================================

export interface UseInterviewMutationsResult {
  createInterview: (data: CreateInterviewRequest) => Promise<string>;
  updateInterview: (data: UpdateInterviewRequest) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
}

export function useInterviewMutations(): UseInterviewMutationsResult {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createInterview = async (data: CreateInterviewRequest): Promise<string> => {
    if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

    setIsSubmitting(true);
    setError(null);

    try {
      const create = httpsCallable<CreateInterviewRequest, CreateInterviewResponse>(
        functions,
        'createInterview'
      );

      const result = await create(data);

      if (result.data.success) {
        return result.data.id;
      } else {
        throw new Error(result.data.message || '면담 저장 실패');
      }
    } catch (err: any) {
      // 상세 에러 로깅 강화
      console.error('[createInterview] Error:', {
        code: err?.code,
        message: err?.message,
        details: err?.details,
        customData: err?.customData,
        name: err?.name,
      });
      console.error('[createInterview] Full error:', err);

      // 에러 메시지 구성 (details가 있으면 step/reason 포함)
      let message = err.message || '면담 저장 중 오류가 발생했습니다.';
      if (err?.details?.step) {
        message = `${message} (step: ${err.details.step})`;
      }
      if (err?.customData?.step) {
        message = `${message} (step: ${err.customData.step})`;
      }

      setError(message);
      throw new Error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateInterview = async (data: UpdateInterviewRequest): Promise<void> => {
    if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

    setIsSubmitting(true);
    setError(null);

    try {
      const update = httpsCallable<UpdateInterviewRequest, UpdateInterviewResponse>(
        functions,
        'updateInterview'
      );

      const result = await update(data);

      if (!result.data.success) {
        throw new Error(result.data.message || '면담 수정 실패');
      }
    } catch (err: any) {
      // 상세 에러 로깅 강화
      console.error('[updateInterview] Error:', {
        code: err?.code,
        message: err?.message,
        details: err?.details,
        customData: err?.customData,
      });
      console.error('[updateInterview] Full error:', err);

      let message = err.message || '면담 수정 중 오류가 발생했습니다.';
      if (err?.details?.step || err?.customData?.step) {
        const step = err?.details?.step || err?.customData?.step;
        message = `${message} (step: ${step})`;
      }

      setError(message);
      throw new Error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    createInterview,
    updateInterview,
    isSubmitting,
    error,
  };
}

// ============================================
// getSignatureUrl은 ../utils/storage.ts로 이동
// 상단에서 re-export: export { getSignatureUrl } from '../utils/storage';
// ============================================

// ============================================
// 면담 삭제 함수
// ============================================

interface DeleteInterviewResponse {
  success: boolean;
  message?: string;
}

/**
 * 면담 삭제
 * @param id 면담 문서 ID
 */
export async function deleteInterview(id: string): Promise<void> {
  if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

  const deleteFn = httpsCallable<{ id: string }, DeleteInterviewResponse>(
    functions,
    'deleteInterview'
  );

  const result = await deleteFn({ id });

  if (!result.data.success) {
    throw new Error(result.data.message || '면담 삭제 실패');
  }
}

// ============================================
// 면담 전송 함수
// ============================================

interface SendInterviewResponse {
  success: boolean;
  message?: string;
}

/**
 * 면담 전송 (대상자에게)
 * @param id 면담 문서 ID
 */
export async function sendInterview(id: string): Promise<void> {
  if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

  const sendFn = httpsCallable<{ id: string }, SendInterviewResponse>(
    functions,
    'sendInterview'
  );

  const result = await sendFn({ id });

  if (!result.data.success) {
    throw new Error(result.data.message || '면담 전송 실패');
  }
}

// ============================================
// 저장 + 전송 통합 함수
// ============================================

/** 저장+전송 요청 타입 */
export interface SaveAndSendInterviewRequest {
  interviewId?: string;  // 있으면 수정/재전송, 없으면 신규 생성
  targetUserId: string;
  targetUserName?: string;
  targetUserOrgUnitName?: string;
  targetUserPositionTitle?: string;
  interviewDate: string;
  interviewTime: string;
  location: string;
  type: InterviewType;
  contentHtml: string;
  signatureBase64: string;  // 필수
}

interface SaveAndSendInterviewResponse {
  success: boolean;
  id: string;
  message?: string;
}

/**
 * 면담 저장 + 전송 통합 함수
 *
 * UX 개선 - "대상자에게 전송" 버튼 클릭 시 저장과 전송을 원자적으로 처리
 *
 * @param data 저장+전송 요청 데이터
 * @returns 생성/수정된 면담 ID
 */
export async function saveAndSendInterview(data: SaveAndSendInterviewRequest): Promise<string> {
  if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

  const saveAndSendFn = httpsCallable<SaveAndSendInterviewRequest, SaveAndSendInterviewResponse>(
    functions,
    'saveAndSendInterview'
  );

  try {
    const result = await saveAndSendFn(data);

    if (!result.data.success) {
      throw new Error(result.data.message || '면담 저장+전송 실패');
    }

    return result.data.id;
  } catch (err: any) {
    // 상세 에러 로깅
    console.error('[saveAndSendInterview] Error:', {
      code: err?.code,
      message: err?.message,
      details: err?.details,
      customData: err?.customData,
    });

    // details(missingFields 등)를 보존하여 FE에서 활용 가능하도록
    // Firebase HttpsError는 details를 customData에 담아서 전달
    const details = err?.details || err?.customData;

    // 에러 객체에 details 포함하여 throw
    const enrichedError: any = new Error(err.message || '면담 저장+전송 중 오류가 발생했습니다.');
    enrichedError.code = err?.code;
    enrichedError.details = details;

    throw enrichedError;
  }
}

// ============================================
// 내가 받은 면담 목록 조회 Hook
// ============================================

interface GetMyReceivedInterviewsResponse {
  success: boolean;
  interviews: Interview[];
}

export interface UseMyReceivedInterviewsResult {
  interviews: Interview[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * 본인에게 전송된 면담 목록 조회
 * 서버에서 auth.uid를 targetUserId로 고정 (보안)
 */
export function useMyReceivedInterviews(): UseMyReceivedInterviewsResult {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchInterviews = useCallback(async () => {
    if (!functions) {
      setInterviews([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getMyInterviews = httpsCallable<void, GetMyReceivedInterviewsResponse>(
        functions,
        'getMyReceivedInterviews'
      );

      const result = await getMyInterviews();

      if (result.data.success) {
        setInterviews(result.data.interviews);
      } else {
        throw new Error('내 면담 목록 조회 실패');
      }
    } catch (err: any) {
      // 상세 에러 로깅
      console.error('[useMyReceivedInterviews] Error:', {
        code: err?.code,
        message: err?.message,
        details: err?.details,
        customData: err?.customData,
      });
      console.error('[useMyReceivedInterviews] Full error:', err);

      // step 정보 추출
      const step = err?.details?.step || err?.customData?.step || 'unknown';
      const reason = err?.details?.reason || err?.customData?.reason || err?.message || '';

      // 사용자 친화적 에러 메시지
      const userMessage = `면담기록 조회 실패: ${err?.code || 'INTERNAL'} (step: ${step})`;
      console.error('[useMyReceivedInterviews] User message:', userMessage, 'reason:', reason);

      setError(new Error(userMessage));
      setInterviews([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInterviews();
  }, [fetchInterviews]);

  return {
    interviews,
    isLoading,
    error,
    refetch: fetchInterviews,
  };
}
